/* ============================================================
   KAIRII — My Shelf (collection tracker) behaviour
   Vanilla JS, no framework. Owned state persists in localStorage,
   keyed per slot by `data-owned-key` (a product id for sold figures,
   or `mo:<metaobject-handle>` for tracked-only figures). Pieces bought
   from Kairii (window.__krShelfPurchased, injected from the logged-in
   customer's orders) auto-mark as "Acquired here".
   ============================================================ */
(function () {
  'use strict';
  var SAVE_URL = window.__krShelfSaveUrl || null;          // App Proxy write endpoint (logged-in only)
  var LOGGED_IN = !!window.__krShelfLoggedIn;
  // Guests and logged-in customers use SEPARATE localStorage keys, so anything you
  // change while logged out can never overwrite your account. The account metafield is
  // the source of truth when logged in; the per-account cache just speeds up navigation.
  var GUEST_KEY = 'kairii-shelf-v1';
  // Per-customer cache key so two accounts on the same browser never share a shelf.
  var ACCT_KEY = 'kairii-shelf-acct-' + (window.__krShelfCustomerId || '0');
  var KEY = LOGGED_IN ? ACCT_KEY : GUEST_KEY;
  var store = {};
  try { store = JSON.parse(localStorage.getItem(KEY)) || {}; } catch (e) { store = {}; }
  // READONLY is true ONLY when viewing a shared shelf (?shelf=…). It hard-gates every write so a
  // shared view can never touch the viewer's own localStorage cache or account metafield.
  // Detect it HERE (cheap, DOM-independent) — before the account merge + login save-back below —
  // so opening someone else's shared link can never queue a write of their snapshot to your account.
  // Use the SAME truthiness test the decode below uses (a present, non-empty `shelf` value),
  // so the early gate and the actual shared-view decode can never disagree — e.g. a bare
  // `?shelf=` (empty value) must NOT strand a logged-in viewer in a save-locked, un-merged shelf.
  var READONLY = false;
  try { READONLY = !!new URLSearchParams(location.search).get('shelf'); } catch (e) {}

  // Account-bound persistence (Option A): when logged in, the source of truth is the
  // customer's `custom.shelf` metafield (injected server-side). Merge it with the
  // per-account cache (both are logged-in writes) most-recent-wins, so an in-session
  // toggle survives a page change before the debounced save lands. Guest edits live
  // under GUEST_KEY and are deliberately NOT merged in — that was the bug that let a
  // logged-out un-tick wipe the account.
  if (!READONLY && LOGGED_IN && window.__krShelfServer && typeof window.__krShelfServer === 'object') {
    var merged = {};
    [window.__krShelfServer, store].forEach(function (s) {
      for (var k in s) {
        if (s.hasOwnProperty(k) && (!merged[k] || (s[k].date || 0) >= (merged[k].date || 0))) merged[k] = s[k];
      }
    });
    // Brand-new account (nothing on the server or in the account cache yet): adopt the
    // guest's progress once, so a first-time sign-up doesn't lose what they ticked.
    if (Object.keys(merged).length === 0) {
      try { merged = JSON.parse(localStorage.getItem(GUEST_KEY)) || {}; } catch (e) {}
    }
    store = merged;
  }

  // Debounced write-back to the metafield via the App Proxy. Fire-and-forget: if the
  // endpoint isn't configured yet (or fails), the local cache still holds the state,
  // so nothing breaks before the proxy is deployed.
  var saveTimer = 0;
  function scheduleServerSave() {
    if (!LOGGED_IN || !SAVE_URL) return;
    clearTimeout(saveTimer);
    saveTimer = setTimeout(function () {
      if (READONLY) return;   // a shared view was entered after this was queued — never persist it
      try {
        fetch(SAVE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ shelf: store })
        }).catch(function () {});
      } catch (e) {}
    }, 700);
  }

  function save() {
    if (READONLY) return;   // shared-view: display only, never persist
    try { localStorage.setItem(KEY, JSON.stringify(store)); } catch (e) {}
    scheduleServerSave();
  }

  // Persist the merged state once on login (syncs any local-only items up to the account).
  if (LOGGED_IN && window.__krShelfServer) { save(); }

  function isOwned(id) { return !!(store[id] && store[id].owned); }
  // Wishlist ("want") rides in the SAME record as owned, so it persists + syncs through the
  // identical account-bound path (localStorage + the custom.shelf metafield). It only counts
  // as "wanted" while NOT owned — once you own it, the chase is over.
  function isWanted(id) { return !!(store[id] && store[id].want && !store[id].owned); }
  function sourceOf(id) { return store[id] && store[id].owned ? store[id].source : null; }
  function setOwned(id, owned, source) {
    var rec = store[id] || {};
    if (owned) store[id] = { owned: true, source: source || 'manual', want: false, date: Date.now() };
    else store[id] = { owned: false, source: rec.source || null, want: !!rec.want, date: Date.now() };
    save();
  }
  function setWant(id, want) {
    var rec = store[id] || {};
    store[id] = { owned: !!rec.owned, source: rec.source || (rec.owned ? 'manual' : null), want: !!want, date: Date.now() };
    save();
  }
  function pad2(n) { return String(n).length < 2 ? '0' + n : String(n); }

  // Parse a heterogeneous release-date string into a sortable YYYYMMDD int (-1 if unknown).
  // Handles JP "2021年6月3日", English "April 28, 2026" / "October 2026", and bare years.
  // Shared by the sort dropdown and the release-calendar view.
  var KR_MONTHS = { january: 1, february: 2, march: 3, april: 4, may: 5, june: 6, july: 7, august: 8, september: 9, october: 10, november: 11, december: 12 };
  function krReleaseKey(s) {
    if (!s) return -1;
    s = String(s);
    var jp = s.match(/(\d{4})年(\d{1,2})月(?:(\d{1,2})日)?/);                          // YYYY年MM月DD日
    if (jp) return (+jp[1]) * 10000 + (+jp[2]) * 100 + (+(jp[3] || 1));
    var en = s.toLowerCase().match(/(january|february|march|april|may|june|july|august|september|october|november|december)\s+(?:(\d{1,2}),?\s+)?(\d{4})/);
    if (en) return (+en[3]) * 10000 + KR_MONTHS[en[1]] * 100 + (+(en[2] || 1));
    var yr = s.match(/(19|20)\d{2}/);                                                  // bare year (SDCC 2014, World Adventure 2019…)
    if (yr) return (+yr[0]) * 10000;
    return -1;
  }

  // Swap the modal image cleanly: drop the previous figure immediately (so it never
  // lingers while the new one downloads) and fade the new image in once it has loaded.
  function swapModalImage(img, url, alt, instant) {
    if (!img) return;
    img.onload = img.onerror = null;
    img.alt = alt || '';
    // If we already have a cached low-res of this figure (the shelf tile / the thumbnail),
    // show it INSTANTLY so there's no blank gap, then quietly upgrade to the full-res.
    if (instant) {
      img.classList.remove('is-loading');
      img.src = instant;
      if (instant === url) return;
      var hi = new Image();
      hi.onload = function () { img.src = url; };
      hi.src = url;
      return;
    }
    // No placeholder available: clear the previous figure and fade the new one in on load.
    img.classList.add('is-loading');
    img.removeAttribute('src');
    img.onload = function () { img.classList.remove('is-loading'); };
    img.onerror = function () { img.classList.remove('is-loading'); };
    img.src = url;
    if (img.complete && img.naturalWidth) img.classList.remove('is-loading');
  }

  // A light variant of a width:1100 CDN URL for the thumbnail strip — also reused as the
  // instant placeholder when its thumb is clicked, so switching pictures never blanks out.
  function krThumbUrl(url) {
    return /[?&]width=\d+/.test(url) ? url.replace(/([?&]width=)\d+/, '$1300') : url;
  }

  function initShelf(root) {
    if (root.__krShelf) return;
    root.__krShelf = true;

    var slots = [].slice.call(root.querySelectorAll('[data-kr-slot]'));
    var blocks = [].slice.call(root.querySelectorAll('[data-kr-series-block]'));
    var query = '';   // current search text (lowercased)

    /* ---- shareable shelf: a ?shelf= link carries owned + wishlist as bitmasks over the
       lexicographically-sorted figure keys (identical on both ends for the same catalogue).
       Compact (~1 bit/figure) and fully client-side. ---- */
    var keyOrder = slots.map(function (s) { return s.getAttribute('data-owned-key'); }).sort();
    function b64urlFromBytes(b) { var s = ''; for (var i = 0; i < b.length; i++) s += String.fromCharCode(b[i]); return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''); }
    function bytesFromB64url(str) { str = (str || '').replace(/-/g, '+').replace(/_/g, '/'); while (str.length % 4) str += '='; try { var bin = atob(str), a = new Uint8Array(bin.length); for (var i = 0; i < bin.length; i++) a[i] = bin.charCodeAt(i); return a; } catch (e) { return new Uint8Array(0); } }
    function maskBytes(pred) { var b = new Uint8Array(Math.ceil(keyOrder.length / 8)); keyOrder.forEach(function (k, i) { if (pred(k)) b[i >> 3] |= (1 << (i & 7)); }); return b; }
    function shareUrl() {
      return location.origin + location.pathname + '?shelf=' + keyOrder.length +
        '.' + b64urlFromBytes(maskBytes(isOwned)) + '.' + b64urlFromBytes(maskBytes(isWanted));
    }
    var sharedRaw = null;
    try { sharedRaw = new URLSearchParams(location.search).get('shelf'); } catch (e) {}
    if (sharedRaw) {
      var p = sharedRaw.split('.'), ob = bytesFromB64url(p[1]), wb = bytesFromB64url(p[2]), ss = {};
      keyOrder.forEach(function (k, i) {
        if (ob[i >> 3] & (1 << (i & 7))) ss[k] = { owned: true, source: 'manual', want: false, date: 0 };
        else if (wb[i >> 3] & (1 << (i & 7))) ss[k] = { owned: false, source: null, want: true, date: 0 };
      });
      store = ss;                 // in-memory only — save() is hard-gated by READONLY
      READONLY = true;
      root.classList.add('is-shared');
      var banner = root.querySelector('[data-kr-shared-banner]'); if (banner) banner.hidden = false;
    }

    /* purchases are authoritative provenance — mark them web-owned on load (NOT in a shared view) */
    if (!READONLY) {
      var purchased = window.__krShelfPurchased || [];
      var changed = false;
      purchased.forEach(function (id) {
        id = String(id);
        if (!store[id] || !store[id].owned || store[id].source !== 'web') {
          store[id] = { owned: true, source: 'web', date: (store[id] && store[id].date) || Date.now() };
          changed = true;
        }
      });
      if (changed) save();
    }

    // Prefetch a figure's popup images once its card scrolls on screen (during idle time),
    // so opening the details popup shows everything instantly instead of loading it in.
    // Runs once per card; figures with no extra images are covered by the loaded shelf tile.
    // Honour Save-Data / 2g: those users skip the speculative prefetch (the popup still
    // loads its images on open — they just don't get the head start).
    /* ---- lazily-loaded gallery map: metaobject figure images live in /pages/shelf?view=gallery-data
       (NOT inline) to keep ~420KB + ~2,300 image_url calls off the shelf render. Keyed by the slot's
       data-owned-key (mo:<handle>). Sold products keep their images inline. ---- */
    var galleries = {}, galleriesLoaded = false, galleriesLoading = false, galleriesCbs = [];
    function loadGalleries(cb) {
      if (galleriesLoaded) { if (cb) cb(); return; }
      if (cb) galleriesCbs.push(cb);
      if (galleriesLoading) return;
      galleriesLoading = true;
      // NOTE: no Accept:application/json header — that makes Shopify return the page RESOURCE as JSON
      // ({"page":{…}}) instead of rendering the ?view=gallery-data template. Plain GET → our JSON.
      fetch(location.pathname + '?view=gallery-data')
        .then(function (r) { return r.text(); })
        .then(function (t) {
          try { galleries = (JSON.parse(t) || {}).galleries || {}; } catch (e) {}
          galleriesLoaded = true; galleriesLoading = false;
          galleriesCbs.splice(0).forEach(function (f) { try { f(); } catch (e) {} });
        })
        .catch(function () { galleriesLoading = false; });
    }
    function imagesFor(slot, inline) {
      if (inline && inline.length) return inline;                    // sold product: inline images
      return galleries[slot.getAttribute('data-owned-key')] || [];   // metaobject: from the lazy map
    }
    (window.requestIdleCallback || function (f) { return setTimeout(f, 300); })(function () { loadGalleries(); });

    var krConn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    var krSkipPrefetch = !!(krConn && (krConn.saveData || /(^|-)2g$/.test(krConn.effectiveType || '')));
    if (!krSkipPrefetch && 'IntersectionObserver' in window) {
      var krIdle = window.requestIdleCallback || function (f) { return setTimeout(f, 200); };
      var krPrefetchObs = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          krPrefetchObs.unobserve(e.target);
          var sc = e.target.querySelector('script[data-kr-details]');
          var inline = [];
          if (sc) { try { inline = JSON.parse(sc.textContent).images || []; } catch (_) {} }
          var imgs = imagesFor(e.target, inline).filter(Boolean);
          if (!imgs.length) return;   // metaobject gallery not loaded yet → warmed on a later pass
          krIdle(function () {
            var m = new Image(); m.src = imgs[0];                                          // full-res main
            if (imgs.length > 1) imgs.forEach(function (u) { var t = new Image(); t.src = krThumbUrl(u); }); // strip thumbs
          });
        });
      }, { rootMargin: '300px' });
      slots.forEach(function (sl) { krPrefetchObs.observe(sl); });
    }

    function q(sel) { return root.querySelector(sel); }
    function setText(sel, v) { var el = q(sel); if (el) el.textContent = v; }

    function applySlot(slot, animate) {
      var id = slot.getAttribute('data-owned-key');
      var owned = isOwned(id);
      slot.classList.toggle('is-owned', owned);
      slot.classList.toggle('is-missing', !owned);
      var t = window.__krShelfI18n || {};
      var btn = slot.querySelector('[data-kr-toggle]');
      if (btn) {
        btn.setAttribute('aria-pressed', owned ? 'true' : 'false');
        btn.title = owned ? (t.removeOwned || 'Remove from your collection') : (t.markOwned || 'Mark as owned');
      }
      var wanted = isWanted(id);
      slot.classList.toggle('is-wanted', wanted);
      var wbtn = slot.querySelector('[data-kr-want]');
      if (wbtn) {
        wbtn.setAttribute('aria-pressed', wanted ? 'true' : 'false');
        wbtn.title = wanted ? (t.wantRemove || 'Remove from wishlist') : (t.wantAdd || 'Add to wishlist');
        wbtn.hidden = owned;   // no point wishlisting something you own
      }
      var src = slot.querySelector('[data-kr-src]');
      if (src) {
        if (sourceOf(id) === 'web') { src.classList.add('is-web'); src.innerHTML = '<span class="kmark">K</span>' + (t.acquiredHere || 'Acquired here'); }
        else { src.classList.remove('is-web'); src.textContent = t.addedManually || 'Added manually'; }
      }
      if (animate && owned) {
        slot.classList.add('kr-just-owned');
        setTimeout(function () { slot.classList.remove('kr-just-owned'); }, 760);
      }
    }

    // Cached, lowercased search index for a slot: figure name + variant + series title.
    function slotSearchText(slot) {
      if (slot.__krSearch == null) {
        var nameEl = slot.querySelector('.kr-slot__name');
        var subEl = slot.querySelector('.kr-slot__sub');
        var varEl = slot.querySelector('.kr-slot__variant');
        var block = slot.closest && slot.closest('[data-kr-series-block]');
        var seriesEl = block && block.querySelector('.kr-shelf-series__title h2');
        slot.__krSearch = [
          nameEl && nameEl.textContent, subEl && subEl.textContent, varEl && varEl.textContent, seriesEl && seriesEl.textContent
        ].join(' ').toLowerCase();
      }
      return slot.__krSearch;
    }
    function matchesQuery(slot) {
      return !query || slotSearchText(slot).indexOf(query) !== -1;
    }

    function visibleUnder(filter, series, slot) {
      if (!matchesQuery(slot)) return false;
      if (series !== 'all' && slot.getAttribute('data-series') !== series) return false;
      var id = slot.getAttribute('data-owned-key');
      var owned = isOwned(id);
      if (filter === 'owned' && !owned) return false;
      if (filter === 'missing' && owned) return false;        // "missing" = everything not owned
      if (filter === 'want' && !isWanted(id)) return false;   // "want" = the wishlist subset
      return true;
    }

    function updateVisibility() {
      var filter = root.getAttribute('data-filter');
      var series = root.getAttribute('data-series');
      // text search hides individual slots (owned/missing is CSS-driven; series is block-level)
      slots.forEach(function (s) { s.classList.toggle('kr-search-hide', !matchesQuery(s)); });
      var any = false;
      blocks.forEach(function (block) {
        var bid = block.getAttribute('data-kr-series-block');
        if (series !== 'all' && bid !== series) { block.classList.add('is-hidden'); return; }
        var bslots = [].slice.call(block.querySelectorAll('[data-kr-slot]'));
        var vis = bslots.some(function (s) { return visibleUnder(filter, series, s); });
        block.classList.toggle('is-hidden', !vis);
        if (vis) any = true;
      });
      var empty = q('[data-kr-shelf-empty]');
      if (empty) empty.hidden = any;
      if (root.__krCalVis) root.__krCalVis();   // keep the calendar view's rows in sync with filter/series/search
    }

    function recompute() {
      var total = 0, owned = 0, web = 0, manual = 0, wanted = 0;
      slots.forEach(function (slot) {
        total++;
        var id = slot.getAttribute('data-owned-key');
        if (isOwned(id)) { owned++; if (sourceOf(id) === 'web') web++; else manual++; }
        else if (isWanted(id)) wanted++;
      });
      var pct = total ? Math.round(owned / total * 100) : 0;
      var ring = q('[data-kr-ring]');
      if (ring) ring.style.setProperty('--pct', pct);
      setText('[data-kr-pct]', pct);
      setText('[data-kr-owned]', owned);
      setText('[data-kr-total]', total);
      setText('[data-kr-web]', web);
      setText('[data-kr-manual]', manual);
      setText('[data-kr-want-count]', wanted);
      var wantTag = q('[data-kr-want-tag]'); if (wantTag) wantTag.hidden = wanted === 0;

      blocks.forEach(function (block) {
        var bid = block.getAttribute('data-kr-series-block');
        var bslots = [].slice.call(block.querySelectorAll('[data-kr-slot]'));
        var bt = bslots.length, bo = 0;
        bslots.forEach(function (s) { if (isOwned(s.getAttribute('data-owned-key'))) bo++; });
        var cnt = block.querySelector('[data-kr-series-owned]'); if (cnt) cnt.textContent = pad2(bo);
        var bar = block.querySelector('[data-kr-series-bar]'); if (bar) bar.style.width = (bt ? Math.round(bo / bt * 100) : 0) + '%';
        block.classList.toggle('is-complete', bt > 0 && bo === bt);
        var chipN = q('[data-kr-chip-n="' + cssEsc(bid) + '"]'); if (chipN) chipN.textContent = bo + '/' + bt;
      });
      updateVisibility();
      if (root.__krCalOwned) root.__krCalOwned();   // refresh the calendar view's owned dots
    }

    function cssEsc(s) { return (window.CSS && CSS.escape) ? CSS.escape(s) : s; }

    /* ---- figure-details modal (public — opens for everyone, logged in or out) ---- */
    var modal = q('[data-kr-fig-modal]');
    var lastFocus = null;

    function fillRow(key, val) {
      var row = q('[data-kr-fig-row="' + key + '"]');
      var dd = q('[data-kr-fig-val="' + key + '"]');
      if (!row) return;
      if (val) { if (dd) dd.textContent = val; row.hidden = false; }
      else { row.hidden = true; }
    }

    function modalFocusables() {
      if (!modal) return [];
      return [].slice.call(modal.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])'))
        .filter(function (el) { return !el.hidden && el.offsetParent !== null; });
    }

    function openModal(slot) {
      if (!modal) return;
      var holder = slot.querySelector('[data-kr-details]');
      if (!holder) return;
      var d; try { d = JSON.parse(holder.textContent); } catch (e) { return; }
      var t = window.__krShelfI18n || {};

      setText('[data-kr-fig-name]', d.name || '');
      fillRow('release', d.release);
      fillRow('height', d.height);
      fillRow('edition', d.edition);
      fillRow('contents', d.contents);

      // images: sold products inline them; metaobject figures come from the lazily-loaded galleries map.
      var figKey = slot.getAttribute('data-owned-key');
      modal.__krSlot = slot;   // so a late gallery load only repaints if this slot's modal is still open
      var tileImg = slot.querySelector('.kr-slot__media img');
      var tileSrc = tileImg ? (tileImg.currentSrc || tileImg.src) : '';
      var mainImg = q('[data-kr-fig-img]');
      var noImg = q('[data-kr-fig-noimg]');
      var thumbs = q('[data-kr-fig-thumbs]');
      function paintImages(imgs) {
        imgs = (imgs || []).filter(Boolean);
        if (mainImg) {
          if (imgs.length) {
            mainImg.hidden = false; if (noImg) noImg.hidden = true;
            swapModalImage(mainImg, imgs[0], d.name || '', tileSrc);
          } else if (tileSrc) {
            // gallery not loaded yet (or a single-tile figure): show the tile image, no strip
            mainImg.hidden = false; if (noImg) noImg.hidden = true;
            swapModalImage(mainImg, tileSrc, d.name || '', tileSrc);
          } else {
            mainImg.onload = mainImg.onerror = null; mainImg.removeAttribute('src'); mainImg.classList.remove('is-loading'); mainImg.hidden = true; if (noImg) noImg.hidden = false;
          }
        }
        if (thumbs) {
          thumbs.innerHTML = '';
          if (imgs.length > 1) {
            imgs.forEach(function (u, i) {
              var b = document.createElement('button');
              b.type = 'button';
              b.className = 'kr-fig-thumb' + (i === 0 ? ' is-active' : '');
              var thumbUrl = krThumbUrl(u);
              var im = document.createElement('img'); im.src = thumbUrl; im.alt = ''; im.loading = 'lazy';
              b.appendChild(im);
              b.addEventListener('click', function () {
                // show the already-loaded thumbnail instantly, then upgrade to full-res — no blank gap
                swapModalImage(mainImg, u, mainImg ? mainImg.alt : '', thumbUrl);
                thumbs.querySelectorAll('.kr-fig-thumb').forEach(function (x) { x.classList.remove('is-active'); });
                b.classList.add('is-active');
              });
              thumbs.appendChild(b);
            });
            thumbs.hidden = false;
          } else { thumbs.hidden = true; }
        }
      }
      var inlineImgs = (d.images || []).filter(Boolean);
      paintImages(imagesFor(slot, inlineImgs));
      // metaobject gallery not loaded yet → fetch it and repaint if this modal is still showing this slot
      if (!inlineImgs.length && !(galleries[figKey] && galleries[figKey].length) && !galleriesLoaded) {
        loadGalleries(function () { if (!modal.hidden && modal.__krSlot === slot) paintImages(imagesFor(slot, [])); });
      }

      /* releases (reissues/shipments folded into this card) */
      var relWrap = q('[data-kr-fig-releases]');
      var relList = q('[data-kr-fig-releases-list]');
      if (relList) {
        relList.innerHTML = '';
        var vars = (d.variants || []).filter(function (v) { return v && (v.label || v.date); });
        if (vars.length > 1) {
          vars.forEach(function (v) {
            var li = document.createElement('li');
            li.className = 'kr-fig-release';
            var lab = document.createElement('span');
            lab.className = 'kr-fig-release__label';
            lab.textContent = v.label || '';
            var dt = document.createElement('span');
            dt.className = 'kr-fig-release__date';
            dt.textContent = v.date || '';
            li.appendChild(lab); li.appendChild(dt);
            relList.appendChild(li);
          });
          if (relWrap) relWrap.hidden = false;
        } else if (relWrap) { relWrap.hidden = true; }
      }

      var buyWrap = q('[data-kr-fig-buy-wrap]');
      var buy = q('[data-kr-fig-buy]');
      if (buy && d.buyUrl) {
        buy.href = d.buyUrl;
        if (d.buyExternal) {
          buy.target = '_blank'; buy.rel = 'noopener';
          buy.textContent = d.buyLabel || t.visitRetailer || 'Visit retailer';
          buy.classList.add('is-external');
        } else {
          buy.removeAttribute('target'); buy.removeAttribute('rel');
          buy.textContent = t.availableHere || 'Available';
          buy.classList.remove('is-external');
        }
        if (buyWrap) buyWrap.hidden = false;
      } else if (buyWrap) { buyWrap.hidden = true; }

      lastFocus = document.activeElement;
      modal.hidden = false;
      document.body.classList.add('kr-fig-modal-open');
      var closeBtn = modal.querySelector('button[data-kr-fig-close]');
      if (closeBtn) closeBtn.focus();
    }

    function closeModal() {
      if (!modal || modal.hidden) return;
      modal.hidden = true;
      document.body.classList.remove('kr-fig-modal-open');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    if (modal) {
      modal.querySelectorAll('[data-kr-fig-close]').forEach(function (b) { b.addEventListener('click', closeModal); });
      document.addEventListener('keydown', function (e) {
        if (modal.hidden) return;
        if (e.key === 'Escape') { closeModal(); return; }
        if (e.key === 'Tab') {
          var f = modalFocusables();
          if (!f.length) return;
          var first = f[0], last = f[f.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      });
    }

    /* ---- holo tilt on owned slots (gated by the global Holo tweak) — ONE delegated mousemove on
       the shelf root instead of a pair of listeners per slot (300+ figures). ---- */
    var tiltRaf = 0, tiltSlot = null;
    function resetTilt() {
      if (!tiltSlot) return;
      tiltSlot.classList.remove('tilting');
      tiltSlot.style.setProperty('--rx', '0deg'); tiltSlot.style.setProperty('--ry', '0deg');
      tiltSlot = null;
    }
    function bindTilt(container) {
      container.addEventListener('mousemove', function (e) {
        if (document.documentElement.getAttribute('data-kr-holo') !== 'on') return;
        var slot = e.target.closest ? e.target.closest('.kr-slot') : null;
        if (!slot || !slot.classList.contains('is-owned')) { resetTilt(); return; }
        if (tiltSlot && tiltSlot !== slot) resetTilt();
        tiltSlot = slot;
        var r = slot.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width, py = (e.clientY - r.top) / r.height;
        cancelAnimationFrame(tiltRaf);
        tiltRaf = requestAnimationFrame(function () {
          slot.classList.add('tilting');
          slot.style.setProperty('--rx', ((px - 0.5) * 10).toFixed(2) + 'deg');
          slot.style.setProperty('--ry', ((0.5 - py) * 10).toFixed(2) + 'deg');
          slot.style.setProperty('--fx', (px * 100).toFixed(1) + '%');
          slot.style.setProperty('--fy', (py * 100).toFixed(1) + '%');
        });
      });
      container.addEventListener('mouseleave', function () { cancelAnimationFrame(tiltRaf); resetTilt(); });
    }

    /* ---- wire up slots ---- */
    slots.forEach(function (slot) {
      applySlot(slot, false);
      var btn = slot.querySelector('[data-kr-toggle]');
      if (btn) btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (READONLY) return;   // viewing a shared shelf — don't let it edit the viewer's collection
        var id = slot.getAttribute('data-owned-key');
        var willOwn = !isOwned(id);
        setOwned(id, willOwn, 'manual');
        applySlot(slot, willOwn);
        recompute();
      });
      var info = slot.querySelector('[data-kr-info]');
      if (info) info.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        openModal(slot);
      });
      var wbtn = slot.querySelector('[data-kr-want]');
      if (wbtn) wbtn.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        if (READONLY) return;
        var id = slot.getAttribute('data-owned-key');
        setWant(id, !isWanted(id));
        applySlot(slot, false);
        recompute();
      });
    });
    bindTilt(root);   // delegated holo-tilt for all slots

    /* ---- filter segmented control ---- */
    root.querySelectorAll('[data-kr-filter]').forEach(function (b) {
      b.addEventListener('click', function () {
        root.setAttribute('data-filter', b.getAttribute('data-kr-filter'));
        root.querySelectorAll('[data-kr-filter]').forEach(function (x) {
          var on = x === b;
          x.classList.toggle('is-active', on);
          x.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        updateVisibility();
      });
    });

    /* ---- series chips (options live inside the dropdown below) ---- */
    root.querySelectorAll('[data-kr-series-chip]').forEach(function (b) {
      b.addEventListener('click', function () {
        root.setAttribute('data-series', b.getAttribute('data-kr-series-chip'));
        root.querySelectorAll('[data-kr-series-chip]').forEach(function (x) { x.classList.toggle('is-active', x === b); });
        updateVisibility();
      });
    });

    /* ---- series dropdown: open/close + keep the trigger label in sync ----
       The options reuse [data-kr-series-chip], so the handler above already does the
       filtering + is-active toggling; this only opens/closes the menu and mirrors the
       active option's name onto the trigger. */
    (function () {
      var sel = q('[data-kr-series-select]');
      if (!sel) return;
      var trigger = sel.querySelector('[data-kr-series-trigger]');
      var menu = sel.querySelector('[data-kr-series-menu]');
      var label = sel.querySelector('[data-kr-series-label]');
      if (!trigger || !menu) return;

      function open() { sel.classList.add('is-open'); trigger.setAttribute('aria-expanded', 'true'); menu.hidden = false; }
      function close() { sel.classList.remove('is-open'); trigger.setAttribute('aria-expanded', 'false'); menu.hidden = true; }
      function syncLabel() {
        var active = sel.querySelector('[data-kr-series-chip].is-active');
        if (!active || !label) return;
        var nm = active.querySelector('.kr-shelf-series-opt__name');
        label.textContent = nm ? nm.textContent.trim() : active.textContent.trim();
        sel.querySelectorAll('[data-kr-series-chip]').forEach(function (o) {
          o.setAttribute('aria-selected', o === active ? 'true' : 'false');
        });
      }

      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        if (sel.classList.contains('is-open')) close(); else open();
      });
      menu.querySelectorAll('[data-kr-series-chip]').forEach(function (opt) {
        opt.addEventListener('click', function () { syncLabel(); close(); });
      });
      document.addEventListener('click', function (e) {
        if (sel.classList.contains('is-open') && !sel.contains(e.target)) close();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && sel.classList.contains('is-open')) { close(); trigger.focus(); }
      });
      // the empty-state reset link also flips series back to "all" → mirror it
      var resetBtn = q('[data-kr-reset]');
      if (resetBtn) resetBtn.addEventListener('click', function () { syncLabel(); close(); });

      syncLabel();
    })();

    /* ---- sort dropdown: reorder slots WITHIN each series grid by release date ----
       release_date strings are heterogeneous (English "April 28, 2026", JP "2021年6月3日",
       events "SDCC 2014") so we parse a sortable key per slot; undated figures sort last. */
    (function () {
      var sel = q('[data-kr-sort-select]');
      if (!sel) return;
      var trigger = sel.querySelector('[data-kr-sort-trigger]');
      var menu = sel.querySelector('[data-kr-sort-menu]');
      var label = sel.querySelector('[data-kr-sort-label]');
      if (!trigger || !menu) return;

      // release-date parsing lives at module scope now (krReleaseKey) — shared with the calendar view.
      // Sort keys (_krOrder/_krDate/_krName) cost 300+ JSON.parses; nobody sorts on load, so build
      // them lazily the first time a sort actually runs instead of during page init.
      var sortKeysReady = false;
      function ensureSortKeys() {
        if (sortKeysReady) return;
        sortKeysReady = true;
        slots.forEach(function (slot, i) {
          slot._krOrder = i;
          var rel = '', holder = slot.querySelector('[data-kr-details]');
          if (holder) { try { rel = (JSON.parse(holder.textContent) || {}).release || ''; } catch (e) {} }
          slot._krDate = krReleaseKey(rel);
          var nm = slot.querySelector('.kr-slot__name');
          slot._krName = (nm ? nm.textContent : '').trim().toLowerCase();
        });
      }

      function applySort(mode) {
        ensureSortKeys();
        blocks.forEach(function (block) {
          var grid = block.querySelector('.kr-shelf-grid');
          if (!grid) return;
          var items = [].slice.call(grid.querySelectorAll('[data-kr-slot]'));
          items.sort(function (a, b) {
            if (mode === 'default') return a._krOrder - b._krOrder;
            if (mode === 'alpha') return a._krName < b._krName ? -1 : a._krName > b._krName ? 1 : a._krOrder - b._krOrder;
            if (mode === 'owned') {
              var oa = isOwned(a.getAttribute('data-owned-key')), ob = isOwned(b.getAttribute('data-owned-key'));
              if (oa !== ob) return oa ? -1 : 1;             // owned first, then original order
              return a._krOrder - b._krOrder;
            }
            var ka = a._krDate, kb = b._krDate, na = ka < 0, nb = kb < 0;
            if (na && nb) return a._krOrder - b._krOrder;   // both undated → original order
            if (na) return 1;                                // undated always sinks to the end
            if (nb) return -1;
            return mode === 'oldest' ? ka - kb : kb - ka;
          });
          var frag = document.createDocumentFragment();
          items.forEach(function (it) { frag.appendChild(it); });
          grid.appendChild(frag);
        });
      }

      function open() { sel.classList.add('is-open'); trigger.setAttribute('aria-expanded', 'true'); menu.hidden = false; }
      function close() { sel.classList.remove('is-open'); trigger.setAttribute('aria-expanded', 'false'); menu.hidden = true; }
      trigger.addEventListener('click', function (e) { e.stopPropagation(); if (sel.classList.contains('is-open')) close(); else open(); });
      menu.querySelectorAll('[data-kr-sort]').forEach(function (opt) {
        opt.addEventListener('click', function () {
          var mode = opt.getAttribute('data-kr-sort');
          root.setAttribute('data-sort', mode);
          menu.querySelectorAll('[data-kr-sort]').forEach(function (x) {
            var on = x === opt; x.classList.toggle('is-active', on); x.setAttribute('aria-selected', on ? 'true' : 'false');
          });
          var nm = opt.querySelector('.kr-shelf-series-opt__name');
          if (label) label.textContent = nm ? nm.textContent.trim() : opt.textContent.trim();
          applySort(mode);
          close();
        });
      });
      document.addEventListener('click', function (e) { if (sel.classList.contains('is-open') && !sel.contains(e.target)) close(); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && sel.classList.contains('is-open')) { close(); trigger.focus(); } });
    })();

    /* ---- release-calendar view: a date-grouped projection of the same slots ----
       Built lazily on first toggle. Reuses krReleaseKey (module scope) for the messy release
       strings; groups by YYYY-MM, newest month first, undated last. Each row references its source
       slot, so the details modal, owned state, and filter/series/search all stay shared with the
       shelf view (updateVisibility + recompute call back into __krCalVis / __krCalOwned). ---- */
    (function () {
      var calWrap = q('[data-kr-calendar]');
      var viewBtns = [].slice.call(root.querySelectorAll('[data-kr-view]'));
      if (!calWrap || !viewBtns.length) return;
      var built = false;
      var MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

      function relOf(slot) {
        var holder = slot.querySelector('[data-kr-details]');
        if (!holder) return '';
        try { return (JSON.parse(holder.textContent) || {}).release || ''; } catch (e) { return ''; }
      }

      function buildRow(slot, raw) {
        var t = window.__krShelfI18n || {};
        var row = document.createElement('button');
        row.type = 'button';
        row.className = 'kr-cal-row';
        row._krSlot = slot;
        row.setAttribute('data-owned-key', slot.getAttribute('data-owned-key'));

        var thumb = document.createElement('span');
        thumb.className = 'kr-cal-row__thumb';
        var tileImg = slot.querySelector('.kr-slot__media img');
        var nmEl = slot.querySelector('.kr-slot__name');
        if (tileImg) {
          var im = document.createElement('img');
          im.src = tileImg.currentSrc || tileImg.src; im.alt = ''; im.loading = 'lazy';
          thumb.appendChild(im);
        } else {
          var ph = document.createElement('span');
          ph.className = 'kr-cal-row__ph';
          ph.textContent = nmEl ? nmEl.textContent.trim().slice(0, 1) : '◆';
          thumb.appendChild(ph);
        }

        var main = document.createElement('span'); main.className = 'kr-cal-row__main';
        var name = document.createElement('span'); name.className = 'kr-cal-row__name';
        var subEl = slot.querySelector('.kr-slot__sub');
        name.textContent = (nmEl ? nmEl.textContent.trim() : '') + (subEl ? ' · ' + subEl.textContent.trim() : '');
        var series = document.createElement('span'); series.className = 'kr-cal-row__series';
        var block = slot.closest && slot.closest('[data-kr-series-block]');
        var sEl = block && block.querySelector('.kr-shelf-series__title h2');
        series.textContent = sEl ? sEl.textContent.trim() : '';
        main.appendChild(name); main.appendChild(series);

        var right = document.createElement('span'); right.className = 'kr-cal-row__right';
        var date = document.createElement('span'); date.className = 'kr-cal-row__date';
        date.textContent = raw || (t.calUndated || 'Undated');
        var own = document.createElement('span'); own.className = 'kr-cal-row__own'; own.setAttribute('aria-hidden', 'true');
        right.appendChild(date); right.appendChild(own);

        row.appendChild(thumb); row.appendChild(main); row.appendChild(right);
        row.addEventListener('click', function () { openModal(slot); });   // reuse the shared figure-details modal
        return row;
      }

      function build() {
        if (built) return; built = true;
        var t = window.__krShelfI18n || {};
        var buckets = {};
        slots.forEach(function (slot) {
          var raw = relOf(slot);
          var k = krReleaseKey(raw);
          var year = 0, month = 0, bucketKey, sortVal;
          if (k < 0) { bucketKey = 'undated'; sortVal = -1; }
          else {
            year = Math.floor(k / 10000); month = Math.floor((k % 10000) / 100);
            sortVal = year * 100 + month;
            bucketKey = month ? (year + '-' + pad2(month)) : (year + '-00');
          }
          if (!buckets[bucketKey]) buckets[bucketKey] = { key: bucketKey, year: year, month: month, sortVal: sortVal, items: [] };
          buckets[bucketKey].items.push({ slot: slot, raw: raw, k: k });
        });
        var order = Object.keys(buckets).map(function (kk) { return buckets[kk]; });
        order.sort(function (a, b) { return b.sortVal - a.sortVal; });   // newest month first; undated (-1) sinks last

        var frag = document.createDocumentFragment();
        order.forEach(function (b) {
          b.items.sort(function (x, y) { return x.k - y.k; });           // within a month: earliest day first
          var monthEl = document.createElement('div'); monthEl.className = 'kr-cal-month';
          var head = document.createElement('div'); head.className = 'kr-cal-month__head';
          var label = document.createElement('span'); label.className = 'kr-cal-month__label';
          if (b.key === 'undated') label.textContent = t.calUndated || 'Undated';
          else if (b.month) label.textContent = MONTH_NAMES[b.month - 1] + ' ' + b.year;
          else label.textContent = String(b.year);
          var line = document.createElement('span'); line.className = 'kr-cal-month__line';
          var count = document.createElement('span'); count.className = 'kr-cal-month__count'; count.textContent = pad2(b.items.length);
          head.appendChild(label); head.appendChild(line); head.appendChild(count);
          monthEl.appendChild(head);
          var rows = document.createElement('div'); rows.className = 'kr-cal-rows';
          b.items.forEach(function (it) { rows.appendChild(buildRow(it.slot, it.raw)); });
          monthEl.appendChild(rows);
          frag.appendChild(monthEl);
        });
        var emptyEl = document.createElement('p'); emptyEl.className = 'kr-cal-empty'; emptyEl.setAttribute('data-kr-cal-empty', ''); emptyEl.hidden = true;
        emptyEl.textContent = t.calEmpty || 'No releases match this filter.';
        calWrap.appendChild(frag); calWrap.appendChild(emptyEl);
        refreshOwned(); refreshVisibility();
      }

      function refreshOwned() {
        if (!built) return;
        [].slice.call(calWrap.querySelectorAll('.kr-cal-row')).forEach(function (r) {
          r.classList.toggle('is-owned', isOwned(r.getAttribute('data-owned-key')));
        });
      }
      function refreshVisibility() {
        if (!built) return;
        var filter = root.getAttribute('data-filter');
        var series = root.getAttribute('data-series');
        var any = false;
        [].slice.call(calWrap.querySelectorAll('.kr-cal-month')).forEach(function (m) {
          var anyRow = false;
          [].slice.call(m.querySelectorAll('.kr-cal-row')).forEach(function (r) {
            var vis = visibleUnder(filter, series, r._krSlot);
            r.classList.toggle('kr-cal-hide', !vis);
            if (vis) anyRow = true;
          });
          m.classList.toggle('kr-cal-hide', !anyRow);
          if (anyRow) any = true;
        });
        var em = calWrap.querySelector('[data-kr-cal-empty]'); if (em) em.hidden = any;
      }

      // expose so the shared recompute()/updateVisibility() keep the calendar in sync
      root.__krCalOwned = refreshOwned;
      root.__krCalVis = refreshVisibility;

      viewBtns.forEach(function (b) {
        b.addEventListener('click', function () {
          var view = b.getAttribute('data-kr-view');
          if (view === 'calendar') build();                              // build before showing (grids still measured-visible)
          root.setAttribute('data-view', view);
          viewBtns.forEach(function (x) { var on = x === b; x.classList.toggle('is-active', on); x.setAttribute('aria-selected', on ? 'true' : 'false'); });
          if (view === 'calendar') { refreshOwned(); refreshVisibility(); }
        });
      });
    })();

    /* ---- text search (composes with the filter + series above) ---- */
    var searchInput = q('[data-kr-shelf-search]');
    var searchClear = q('[data-kr-shelf-search-clear]');
    if (searchInput) {
      var searchTimer;
      searchInput.addEventListener('input', function () {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(function () {
          query = searchInput.value.trim().toLowerCase();
          if (searchClear) searchClear.hidden = !query;
          updateVisibility();
        }, 110);
      });
    }
    if (searchClear) searchClear.addEventListener('click', function () {
      if (searchInput) { searchInput.value = ''; searchInput.focus(); }
      query = ''; searchClear.hidden = true;
      updateVisibility();
    });

    /* ---- reset link in the empty state ---- */
    var reset = q('[data-kr-reset]');
    if (reset) reset.addEventListener('click', function () {
      if (searchInput) searchInput.value = '';
      query = ''; if (searchClear) searchClear.hidden = true;
      root.setAttribute('data-filter', 'all');
      root.setAttribute('data-series', 'all');
      root.querySelectorAll('[data-kr-filter]').forEach(function (x) {
        var on = x.getAttribute('data-kr-filter') === 'all';
        x.classList.toggle('is-active', on);
        x.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      root.querySelectorAll('[data-kr-series-chip]').forEach(function (x) {
        x.classList.toggle('is-active', x.getAttribute('data-kr-series-chip') === 'all');
      });
      updateVisibility();
    });

    /* ---- share my shelf: Web Share API where available, clipboard fallback otherwise ---- */
    (function () {
      var shareBtn = q('[data-kr-share]');
      if (!shareBtn) return;
      var labelEl = q('[data-kr-share-label]');
      var origLabel = labelEl ? labelEl.textContent : '';
      shareBtn.addEventListener('click', function () {
        var t = window.__krShelfI18n || {};
        var owned = (q('[data-kr-owned]') || {}).textContent || '0';
        var total = (q('[data-kr-total]') || {}).textContent || '0';
        var pct = (q('[data-kr-pct]') || {}).textContent || '0';
        var topName = '', topN = -1;
        blocks.forEach(function (block) {
          var bo = 0;
          [].slice.call(block.querySelectorAll('[data-kr-slot]')).forEach(function (s) { if (isOwned(s.getAttribute('data-owned-key'))) bo++; });
          if (bo > topN) { topN = bo; var h = block.querySelector('.kr-shelf-series__title h2'); topName = h ? h.textContent.trim() : ''; }
        });
        var text = (t.shareText || 'My Kairii shelf — {owned}/{total} figures collected ({pct}%).')
          .replace('{owned}', owned).replace('{total}', total).replace('{pct}', pct);
        if (topN > 0 && topName) text += ' ' + (t.shareSeries || 'Top series: {series}.').replace('{series}', topName);
        var url = shareUrl();   // link carries the collection (owned + wishlist) so it renders on open
        if (navigator.share) {
          navigator.share({ title: t.shareTitle || 'Kairii — My Shelf', text: text, url: url }).catch(function () {});
        } else {
          var full = text + ' ' + url;
          var done = function () { if (labelEl) { labelEl.textContent = t.shareCopied || 'Link copied'; setTimeout(function () { labelEl.textContent = origLabel; }, 1800); } };
          if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(full).then(done, done);
          else { try { window.prompt(t.copyPrompt || 'Copy your shelf link:', full); } catch (e) {} }
        }
      });
    })();

    recompute();

    /* keep multiple tabs / the PDP owned-chip in sync */
    window.addEventListener('storage', function (e) {
      if (READONLY) return;   // shared view shows the shared snapshot, not the viewer's tabs
      if (e.key !== KEY) return;
      try { store = JSON.parse(e.newValue) || {}; } catch (err) {}
      slots.forEach(function (s) { applySlot(s, false); });
      recompute();
    });
  }

  function boot() { document.querySelectorAll('[data-kr-shelf]').forEach(initShelf); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
